import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { PostDetailsContentComponent } from '@src/app/components/news-messages/components/post-details-content/post-details-content.component';
import { NewsMessagesHttpService } from '@src/app/core/http/news-messages-http.service';
import { ApiHeadersInterceptor } from '@src/app/core/interceptors/api-headers.interceptor';
import { GlobalFilterStoredService } from '@src/app/core/services/global-filter-stored.service';
import { SessionService } from '@src/app/core/session.service';
import { SharedModule } from '@src/app/shared';
import { DateFormat } from '@src/app/shared/enums/date.enum';
import { EntityName } from '@src/app/shared/enums/entity-name.enum';
import {
  IconType,
  InternalIcon,
} from '@src/app/shared/enums/internal-icon.enum';
import { NewsMessageInterface } from '@src/app/shared/interfaces/news-message.interface';
import { YouTransformPipe } from '@src/app/shared/pipes/you-transform.pipe';
import { FormUtils } from '@src/app/shared/utils/form-utils';
import { StringUtils } from '@src/app/shared/utils/string-utils';
import { environment } from '@src/environments/environment';
import { BehaviorSubject, catchError, filter, mergeMap, of, take } from 'rxjs';

@Component({
  selector: 'jip-post-details',
  templateUrl: './post-details.component.html',
  standalone: true,
  imports: [CommonModule, SharedModule, PostDetailsContentComponent],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiHeadersInterceptor,
      multi: true,
    },
  ],
})
export class PostDetailsComponent implements OnInit {
  @Input('post_id') postId: string;
  @Input('show_author') showAuthor: boolean = true;
  @Input('show_date') showDate: boolean = true;
  @Input('show_tags') showTags: boolean = true;
  @Input('show_related_posts') showRelatedPosts: boolean = true;
  @Input('bg_color') bgColor: string;

  // Interface for strong typing
  @Input() details: NewsMessageInterface;

  // Properties for template
  isLoadingAttributeDescriptions$ = new BehaviorSubject<boolean>(false);
  rawHtml: SafeHtml;
  currentOrg: any;
  streams: any[] = [];
  isSubmitting = false;
  IconType = IconType;
  InternalIcon = InternalIcon;
  DateFormat = DateFormat;
  peopleURL = environment.jipUrl.people;
  copied: boolean = false;

  get streamNames(): string {
    return (
      this.streams
        ?.filter((stream) =>
          this.details?.content?.streams?.includes(stream.id)
        )
        .map((stream) => stream.name)
        .join(', ') || ''
    );
  }

  constructor(
    readonly sessionService: SessionService,
    private readonly youTransformPipe: YouTransformPipe,
    private readonly newsService: NewsMessagesHttpService,
    private readonly globalFilterStoredService: GlobalFilterStoredService,
    private readonly sanitizer: DomSanitizer,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    let postId = this.postId || this.route.snapshot.paramMap.get('id');
    this.loadPostDetails(postId);
  }

  loadPostDetails(postId: string): void {
    this.isLoadingAttributeDescriptions$.next(true);
    this.sessionService.apiReady$
      .pipe(
        filter((ready) => ready),
        take(1),
        mergeMap(() => this.newsService.read(Number(postId))),
        catchError(() => {
          return of(null);
        })
      )
      .subscribe({
        next: (post: NewsMessageInterface) => {
          this.details = post;
          if (!this.canViewDetails()) {
            this.isLoadingAttributeDescriptions$.next(false);
            this.sessionService.setLoginCallBackpage(location.href);
            FormUtils.navigateTo(
              this.sessionService.appendLanguagePath(environment.jipUrl.login)
            );
            return;
          }
          this.rawHtml = this.getSafeHtml(post?.message || '');
          // this.getCurrentOrg(post);
          this.streams = post?.content?.streams || [];
          this.isLoadingAttributeDescriptions$.next(false);
        },
        error: (error) => {
          console.error('Error loading post details:', error);
          this.details = null;
          this.isLoadingAttributeDescriptions$.next(false);
        },
      });
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getSender(): string {
    return this.youTransformPipe.transform(
      this.details?.from,
      this.sessionService.currentUser?.id
    );
  }

  canViewDetails(): boolean {
    return (
      (this.details && this.details?.isPublic) || this.sessionService.isLogin
    );
  }

  copyPostLink(): void {
    this.copied = true;
    const url = this.generateEntityUrl(EntityName.Post, this.details?.id);
    const baseDomain = window.location.protocol + '//' + window.location.host;
    navigator.clipboard.writeText(baseDomain + url);
  }

  goBack(): void {
    const url = this.generateBaseEntityUrl(EntityName.Post);
    FormUtils.navigateTo(url);
  }

  generateBaseEntityUrl(entityName: string): string {
    const entityUrl = StringUtils.getEntityUrlByEntityName(EntityName.News);
    if (entityUrl) {
      return this.sessionService.appendLanguagePath(`${entityUrl}`);
    }
    return '';
  }

  generateEntityUrl(entityName: string, entityId: number): string {
    if (!entityName) {
      return '';
    }
    const queryParams = `id=${entityId}`;
    const entityUrl = StringUtils.getEntityUrlByEntityName(entityName);
    if (entityUrl) {
      return this.sessionService.appendLanguagePath(
        `${entityUrl}?${queryParams}`
      );
    }
    return '';
  }
}
